import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { WebsocketService } from '../../services/websocket.service';
import { NotificationService } from '../../services/notification.service';
import { filter } from 'rxjs/operators';

interface ToastNotification {
    id: string;
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    timestamp: Date;
    data?: any;
}

@Component({
    selector: 'app-notification-toast',
    templateUrl: './notification-toast.component.html',
    styleUrls: ['./notification-toast.component.css']
})
export class NotificationToastComponent implements OnInit, OnDestroy {
    notifications: ToastNotification[] = [];
    private subscriptions: Subscription[] = [];
    private recentNotifications = new Set<string>();
    private readonly DUPLICATE_WINDOW_MS = 5000; // 5 seconds window for duplicates
    private readonly MAX_NOTIFICATIONS = 3; // Reduced from 5 to 3
    private readonly AUTO_REMOVE_DELAY = 8000; // Reduced from 10 to 8 seconds
    private isComponentActive = true; // Track if component should show notifications

    constructor(
        public websocketService: WebsocketService,
        private notificationService: NotificationService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.subscribeToNotifications();
        this.subscribeToServiceUpdates();
        this.subscribeToLocalNotifications();
        this.subscribeToRouteChanges();
        this.requestNotificationPermission();
    }

    ngOnDestroy(): void {
        this.isComponentActive = false;
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.clearAllNotifications();
        this.recentNotifications.clear();
        // Global history is maintained in the service for cross-route deduplication
    }

    private subscribeToNotifications(): void {

        const notificationSub = this.websocketService.getNotifications().subscribe(data => {

            if (data && this.shouldShowNotification(data)) {

                this.addNotification({
                    id: this.generateId(),
                    title: data.title || 'Notificación',
                    message: data.message || '',
                    type: data.type || 'success',
                    timestamp: new Date(),
                    data: data.data
                });

                // Show browser notification
                this.showBrowserNotification(data.title, data.message);
            } else if (data) {
            } else {
            }
        });

        this.subscriptions.push(notificationSub);
    }

    private subscribeToServiceUpdates(): void {
        const serviceSub = this.websocketService.getServiceStatusUpdates().subscribe(data => {
            if (data && this.shouldShowNotification(data, 'service_update')) {
                this.addNotification({
                    id: this.generateId(),
                    title: 'Actualización de Servicio',
                    message: `El servicio #${data.service_id} ha cambiado de estado`,
                    type: 'info',
                    timestamp: new Date(),
                    data: data
                });
            }
        });

        this.subscriptions.push(serviceSub);
    }

    private subscribeToLocalNotifications(): void {
        
        const localNotificationSub = this.notificationService.getNotifications().subscribe((notifications: ToastNotification[]) => {
            
            // Solo mostrar notificaciones que no estén ya en la lista
            notifications.forEach(notification => {
                if (!this.notifications.find(n => n.id === notification.id)) {
                    this.addNotificationDirectly(notification);
                }
            });
        });

        this.subscriptions.push(localNotificationSub);
    }

    private addNotificationDirectly(notification: ToastNotification): void {
        
        this.notifications.unshift(notification);

        // Keep only the last MAX_NOTIFICATIONS notifications
        if (this.notifications.length > this.MAX_NOTIFICATIONS) {
            const removed = this.notifications.splice(this.MAX_NOTIFICATIONS);
            // Clean up removed notifications from service
            removed.forEach(n => this.notificationService.removeNotification(n.id));
        }

        // Auto-remove after specified delay
        setTimeout(() => {
            this.removeNotification(notification.id);
        }, this.AUTO_REMOVE_DELAY);
    }

    private addNotification(notification: ToastNotification): void {
        this.notifications.unshift(notification);

        // Keep only the last MAX_NOTIFICATIONS notifications
        if (this.notifications.length > this.MAX_NOTIFICATIONS) {
            const removed = this.notifications.splice(this.MAX_NOTIFICATIONS);
            // Clean up removed notifications from service
            removed.forEach(n => this.notificationService.removeNotification(n.id));
        }

        // Auto-remove after specified delay
        setTimeout(() => {
            this.removeNotification(notification.id);
        }, this.AUTO_REMOVE_DELAY);

        // Add to notification service for global state
        this.notificationService.addNotification(notification);
    }

    removeNotification(id: string): void {
        this.notifications = this.notifications.filter(n => n.id !== id);
        this.notificationService.removeNotification(id);
    }

    private showBrowserNotification(title: string, message: string): void {
        this.notificationService.showBrowserNotification(title, message, {
            icon: '/assets/icons/notification-icon.png',
            badge: '/assets/icons/badge-icon.png',
            tag: 'logist-notification',
            requireInteraction: false
        });
    }

    private async requestNotificationPermission(): Promise<void> {
        const permission = await this.notificationService.requestNotificationPermission();
        if (permission !== 'granted') {
            console.warn('Notification permission not granted');
        }
    }

    private generateId(): string {
        return Math.random().toString(36).substring(2, 11);
    }

    getNotificationIcon(type: string): string {
        switch (type) {
            case 'success': return 'ti-check-circle';
            case 'warning': return 'ti-alert-triangle';
            case 'error': return 'ti-x-circle';
            default: return 'ti-info-circle';
        }
    }

    getNotificationClass(type: string): string {
        switch (type) {
            case 'success': return 'toast-success';
            case 'warning': return 'toast-warning';
            case 'error': return 'toast-error';
            default: return 'toast-info';
        }
    }

    onNotificationClick(notification: ToastNotification): void {
        // Handle notification click - could navigate to relevant page
        if (notification.data) {

            // Example: Navigate to service or maintenance detail
            if (notification.data.service_id) {
                // Navigate to service detail
            } else if (notification.data.maintenance_id) {
                // Navigate to maintenance detail
            }
        }

        this.removeNotification(notification.id);
    }

    /**
     * Subscribe to route changes to clear notifications when navigating
     */
    private subscribeToRouteChanges(): void {
        const routeSub = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.clearAllNotifications();
                
                // Temporarily deactivate component to prevent immediate re-showing
                this.isComponentActive = false;
                
                // Reactivate after a short delay to allow route transition to complete
                setTimeout(() => {
                    this.isComponentActive = true;
                }, 500);
            });

        this.subscriptions.push(routeSub);
    }

    /**
     * Check if notification should be shown (duplicate detection)
     */
    private shouldShowNotification(data: any, type: string = 'notification'): boolean {
        // Don't show notifications if component is not active
        if (!this.isComponentActive) {
            return false;
        }

        const title = data.title || 'Notificación';
        const message = data.message || '';

        // Check global history first (survives route changes)
        if (this.notificationService.isNotificationAlreadyShown(title, message)) {
            return false;
        }

        const key = type === 'service_update' 
            ? `service_${data.service_id}_${Math.floor(Date.now() / 30000)}` // Group service updates by 30-second windows
            : this.createNotificationKey({
                title: title,
                message: message,
                timestamp: new Date()
            });

        // Check recent notifications (short term)
        if (this.recentNotifications.has(key)) {
            return false;
        }

        // Mark as shown in both tracking systems
        this.recentNotifications.add(key);
        this.notificationService.markNotificationAsShown(title, message);
        
        // Clean up recent notifications after short window
        setTimeout(() => {
            this.recentNotifications.delete(key);
        }, this.DUPLICATE_WINDOW_MS);

        return true;
    }

    /**
     * Create a unique key for notification deduplication
     */
    private createNotificationKey(notification: { title: string; message: string; timestamp: Date }): string {
        const timeWindow = Math.floor(notification.timestamp.getTime() / this.DUPLICATE_WINDOW_MS);
        return `${notification.title}-${notification.message}-${timeWindow}`;
    }

    /**
     * Clear all notifications
     */
    private clearAllNotifications(): void {
        // Remove all notifications from service
        this.notifications.forEach(n => this.notificationService.removeNotification(n.id));
        
        // Clear local array
        this.notifications = [];
        
        // Clear recent notifications tracking (but keep global history in service)
        this.recentNotifications.clear();
        
    }

    /**
     * Force clear all notification history (for debugging)
     */
    public clearAllHistory(): void {
        this.clearAllNotifications();
        this.notificationService.clearGlobalNotificationHistory();
    }

    trackByFn(index: number, item: ToastNotification): string {
        return item.id;
    }
}
